/**
 * Author: Harsh Agrawal
 * Summary: This page facilitates the creation of a new user account in AWS Cognito.
 */


import React, { useState, useEffect, useRef } from 'react'
import Header from '../../../components/layout/header';
import Footer from '../../../components/layout/footer';
import OrderSummary from '../../../components/orderSummary';
import { useDispatch, useSelector } from 'react-redux';
import { validateForm } from '../../../utils/form';
import { useNavigate } from 'react-router-dom';
import { displayError, displaySuccess } from '../../../reducers/snackbar';
import sepaLogo from './../../../assets/img/sepa-logo.svg'
import bancontactLogo from './../../../assets/img/bancontact-logo.svg'
import appleLogo from './../../../assets/img/apple-logo-black.svg'
import CBCardComponent from '../../../components/cardComponent';
import { createIntent, updateIntent, fetchGatewayAssignment } from '../../../reducers/chargebee';
import { deletePacContainer, focusAndOpenKeyboard } from '../../../utils/globalUtils';
import { updatePaymentIntentId, updatePaymentToken, updateDisplayPurchaseComplete, setEcommerceGTMEvent, setCheckoutPayload, setPendingPayPalPaymentIntentId } from '../../../reducers/cart';
import { initChargebee } from '../../../reducers/chargebee';
import { salesforcePayload } from '../../../utils/functions';
import { createCampaignMember } from '../../../reducers/salesforce';
import { LinearProgress, CircularProgress } from '@mui/material';
import { TRANSLATION_TYPE, USER_PAYMENT_METHOD_PREFERENCE, resolvePayPalCurrencies } from '../../../utils/constants';
import { getTranslation } from '../../../reducers/translation';
import TagManager from 'react-gtm-module';
import { useLocation } from 'react-router-dom';
import { CHECKOUT_STATUS } from '../../../utils/constants';
import { createSubscriptionWithCaptcha, startRecaptchaAssessment } from '../../../reducers/billingDetails';
import { createAccountForNewUser, checkUserStatus } from '../../../reducers/auth';
import Localization from '../../../components/translation';
import { displayPrice } from '../../../components/currencyPrice';
import { setDisplayTax } from '../../../reducers/cart';
import { fetchEstimateForNewSubscription } from '../../../reducers/cart';
import ErrorAlert from '../../../components/Alert';
import { parseError } from '../../../utils/error';
import CheckoutProgress, { SUBMIT_PHASE } from '../../../components/checkoutProgress';



function AddPaymentMethod() {
  const [displayTemporaryPasswordMsg, setDisplayTemporaryPasswordMsg] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const { email, userStatus } = useSelector((state) => (state.auth))
  const { estimate, checkoutPayload, paymentOption, userPaymentMethodPreference, promoCode } = useSelector((state) => state.cart);
  const { nextTotal, trialPeriod, trialPeriodUnit, periodUnit, subStatus, currencyCode, total, thankyouPageType } = estimate;
  const [displayApplePayBtn, setDisplayApplePayBtn] = useState(false);
  const { cbInstance, isCbInitiated, sessionIs3dsEnabled, paymentModule, payPalCurrencies } = useSelector((state) => state.chargebee);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cardRef = useRef(null);
  const [gpayHandler, setGpayHandler] = useState(null);
  const [paymentIntent, setPaymentIntent] = useState({});
  // A card payment intent is single-use. The first submit moves it to "authorized" even when the
  // payment then fails — a 3DS authentication that Braintree rejects at the gateway still leaves the
  // intent authorized — and every later attempt on it is refused with "Payment intent is authorized".
  //
  // Measured on QA 2026-09-16: a customer whose first attempt failed could not retry at all. The
  // button re-enabled, every click produced that message, and the only way forward was reloading the
  // page. Declines are routine, so this stranded a real share of the Braintree arm.
  //
  // The intent created on mount therefore covers the first attempt only; each retry mints a fresh
  // one. Flagged spent *before* authorization, not after, because the intent is consumed whether or
  // not the call succeeds.
  const cardIntentSpent = useRef(false);
  // Don't create a PayPal intent in a currency PayPal isn't offered in — Chargebee has no gateway
  // for it and returns "no applicable gateways found". Gate paypal_express_checkout by the config
  // currency list so this reacts to the /site config without a build.
  const currencyModule = resolvePayPalCurrencies(payPalCurrencies).includes(currencyCode)
    ? paymentModule
    : paymentModule.filter(pm => pm !== 'paypal_express_checkout');
  // Assignment key for the gateway A/B test. Must match what's sent to /checkout/payment-intent so
  // the backend stamps the same gateway this page is branching on.
  const assignmentEmail = checkoutPayload?.email ?? email;
  // null until the assignment resolves. Card only joins payment_module for the 3DS (Braintree)
  // bucket; the control bucket tokenizes and lets Smart Routing pick the gateway, as it does today.
  const assignmentResolved = sessionIs3dsEnabled !== null;
  const payment_module = sessionIs3dsEnabled ? ['card', ...currencyModule] : currencyModule;
  // Starts hidden even when there are no intents to fetch: until the assignment lands we don't know
  // which submit path the card form should take, and showing it early would let a fast customer
  // tokenize their way onto the wrong gateway.
  const [displayPage, setDisplayPage] = useState(false);
  const { gdprEmailUpdates } = useSelector((state) => state.accountDetails);
  const [isWalletUsed, setIsWalletUsed] = useState(false);

  const t = useSelector(getTranslation)
  const t_label = t[TRANSLATION_TYPE.LABEL] ?? {}
  const t_message = t[TRANSLATION_TYPE.MESSAGE] ?? {}
  const [disableBtn, setDisableBtn] = useState(userPaymentMethodPreference === USER_PAYMENT_METHOD_PREFERENCE.APPLE_PAY || userPaymentMethodPreference === USER_PAYMENT_METHOD_PREFERENCE.PAYPAL);
  const [displayPayWithCardLabel, setDisplayPayWithCardLabel] = useState(false);
  // null when idle; otherwise which half of the submit is running. Drives the progress overlay and
  // the unload guard. Card path only — the wallet flows have their own progress treatment.
  const [submitPhase, setSubmitPhase] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [error, setError] = useState();
  const [applePayHandler_, setapplePayHandler_] = useState(null)
  const [paypalHandler_, setPaypalHandler_] = useState(null)
  const [isProcessingPayPalPayment, setIsProcessingPayPalPayment] = useState(false)


  // usePreventBackNavigation();
  deletePacContainer();

  useEffect(() => {
    if (!isCbInitiated) {
      dispatch(initChargebee());
    }
  }, [])

  // Re-runs if the currency arrives after the email: an assignment resolved without a currency lands
  // in control (no gateway account can be matched), and this corrects it well before anyone can
  // submit. Deliberately not gated on currencyCode — a currency that never arrives would otherwise
  // leave the assignment unresolved and the page permanently hidden.
  //
  // Any existing payment intents are discarded first. They were created for the previous email and
  // currency, so they carry the wrong amount and, for card, the wrong gateway account stamped on
  // them by the backend — a currency change is exactly when both are wrong. Clearing here rather
  // than relying on the new ones arriving first means a stale intent cannot be submitted even if the
  // display gating were wrong: handleSubmit mints a fresh intent when it finds none.
  //
  // cardIntentSpent resets with them; the flag describes an intent that no longer exists.
  useEffect(() => {
    setPaymentIntent({});
    cardIntentSpent.current = false;
    dispatch(fetchGatewayAssignment(assignmentEmail, currencyCode));
  }, [assignmentEmail, currencyCode])

  // Hide the form again whenever the assignment is unresolved — on first load, and while a second
  // request is in flight after a late-arriving currency. The card form cannot be shown before we
  // know which submit path it takes, and a brief re-hide is cheaper than letting someone pay through
  // the wrong one.
  //
  // With the assignment known and no payment intents to wait for, there is nothing left to load. The
  // intent-driven case is handled by updatePageLoading instead.
  useEffect(() => {
    if (!assignmentResolved) {
      setDisplayPage(false);
      return;
    }
    if (payment_module.length === 0) {
      setDisplayPage(true);
    }
  }, [assignmentResolved, payment_module.length])

  // NOTE — the ~1.5s of dead air after Complete Purchase cannot be moved from our side. Settled
  // 2026-09-15/16; please don't spend another day on it.
  //
  // chargebee.js only creates the Braintree client inside authorizeWith3ds. Four attempts to warm it
  // at page mount all failed: load('3ds-handler') resolves, load3DSHandler() returns a handler, and
  // handler.setPaymentIntent(intent) succeeds — and every HAR still showed zero Braintree requests
  // before the click. The 3DS handler and the card component do not share a client.
  //
  // We then enumerated both objects rather than keep guessing at the API. cbInstance has 93 methods,
  // none of which prepares a gateway client; the card component has twelve, of which the only
  // Chargebee ones are authorizeWith3ds and tokenize — both of which perform the real operation.
  // There is no prepare/warm entry point to call.
  //
  // The dead air also contains no network requests at all, so it is not a download: it is
  // initialisation plus postMessage traffic with the card iframes, which a HAR cannot see and which
  // inherently cannot run before the card exists.

  // Warn before the page is abandoned mid-submit. The window that matters is COMPLETING: the charge
  // is already in flight with Chargebee, so a refresh there leaves the customer unsure whether they
  // paid while a subscription may well have been created. Browsers show their own generic wording —
  // the text can't be customised — so this only buys the confirmation step, which is the point.
  useEffect(() => {
    if (!submitPhase) {
      return undefined;
    }
    const warnBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [submitPhase])

  useEffect(() => {

    // mountPaymentMethod('google-pay', 'google_pay', '#gpay-button', {
    //     buttonColor: 'white', buttonType: 'plain', buttonSizeMode: 'fill'
    // })
    // Wait for the assignment: payment_module (and so which intents to create) depends on it. Keyed
    // on the value rather than on "is it resolved", so a late-arriving email that flips the bucket
    // re-runs this and creates the card intent the new bucket needs.
    if (cbInstance && assignmentResolved)
      fetchPaymentIntent();
  }, [cbInstance, sessionIs3dsEnabled])

  const createPaymentIntent = async (payment_method_type) => {
    let response = await createIntent({ PaymentMethodType: payment_method_type, CurrencyCode: currencyCode, Amount: Math.round(total * 100), Email: assignmentEmail });
    return response?.payment_intent;
  }

  const updatePaymentIntent = async (payment_intent, newTotal) => {
    // Email goes on the update too — the backend re-resolves the gateway per call, so dropping it
    // here would let an amount resync move a card intent off its assigned gateway.
    let response = await updateIntent({ id: payment_intent.id, PaymentMethodType: payment_intent.payment_method_type, CurrencyCode: payment_intent.currency_code, Amount: Math.round(newTotal * 100), Email: assignmentEmail });
    return response?.payment_intent;
  }

  const fetchPaymentIntent = () => {

    let payment_intent = {};
    let payment_intent_response = {}; // only used to display loading page animation while payment intent is getting fetched


    for (let i = 0; i < payment_module.length; i++) {
      createPaymentIntent(payment_module[i]).then((intent) => {
        payment_intent[payment_module[i]] = intent;
        updatePageLoading(payment_intent);
      }).finally(() => {
        payment_intent_response[payment_module[i]] = true;
        updatePageLoading(payment_intent_response, payment_intent);
      })
    }

  }

  const updatePageLoading = (payment_intent_response, payment_intent) => {

    let isPaymentIntentInitialized = true;

    for (let i = 0; i < payment_module.length; i++) {
      if (!payment_intent_response?.[payment_module[i]])
        isPaymentIntentInitialized = false;
    }

    if (isPaymentIntentInitialized) {
      setDisplayPage(true);
      setPaymentIntent(payment_intent);

      if (payment_intent?.google_pay)
        initGPayCheckout(payment_intent?.google_pay);
      if (payment_intent?.apple_pay)
        initApplePayCheckout(payment_intent?.apple_pay);
      if (payment_intent?.paypal_express_checkout)
        initPayPalCheckout(payment_intent?.paypal_express_checkout);
    }

  }


  const initGPayCheckout = (payment_intent) => {
    if (cbInstance) {
      if (gpayHandler == null) {
        cbInstance.load('google-pay').then((gpayHandler) => {
          setGpayHandler(gpayHandler);
          gpayHandler.setPaymentIntent(payment_intent);
          setDisplayPayWithCardLabel(true);
          gpayHandler.mountPaymentButton('#gpay-button', {
            buttonColor: 'white', buttonType: 'plain', buttonSizeMode: 'fill'
          }).then(() => {
            // once button mounted
            return gpayHandler.handlePayment();
          }).then((result) => {
            dispatch(updatePaymentIntentId({ 'paymentIntentId': result.paymentIntent.id }))
            setIsWalletUsed(true);
            // navigate("/checkout/createaccount");
          }).catch((error) => {
            // handle error
          });
        });
      } else {
        createPaymentIntent('google_pay').then((intent) => {
          gpayHandler.setPaymentIntent(intent);
        })
      }

    }
  };

  const initApplePayCheckout = (payment_intent) => {
    if (cbInstance) {
      if (applePayHandler_ == null) {
        cbInstance.load("apple-pay").then((applePayHandler) => {
          setapplePayHandler_(applePayHandler);
          const isSupportedDevice = applePayHandler.canMakePayments();
          setDisplayApplePayBtn(isSupportedDevice);
          if (isSupportedDevice) {
            applePayHandler.setPaymentIntent(payment_intent);
            setDisplayPayWithCardLabel(true);
            applePayHandler.mountPaymentButton("#apple-pay-button", {
              buttonColor: 'black',
              buttonType: 'check-out',
              onpaymentmethodselected: (event, callback) => {

                let payload = getCheckoutPayload(event?.paymentMethod?.billingContact);

                dispatch(fetchEstimateForNewSubscription(payload)).then((response) => {
                  updateApplePayHandlerWithNewAmount(response?.total, applePayHandler);
                  dispatch(setDisplayTax({ "displayTax": true }));
                  const update = {
                    newTotal: {
                      label: 'Total',
                      amount: response.total
                    }
                  }
                  callback(update)
                }).catch((error) => {
                  dispatch(displayError(error));
                })

              }

            }).then(() => {
              // once button mounted
              return applePayHandler.handlePayment(
                {
                  error: function (d) {
                    console.log("error", d);
                    console.log(d?.active_payment_attempt?.error_detail)
                    dispatch(displayError({ 'error': d?.active_payment_attempt?.error_text }));
                  },
                }

              );
            }).then((paymentIntent) => {
              let billingDetail = paymentIntent?.paymentIntent?.payer_info?.billing_address;
              billingDetail = { ...billingDetail, 'country': billingDetail.countryCode };
              let promoCode = getPromoCode();
              let payload = { ...checkoutPayload, billingDetail, promoCode };
              dispatch(setCheckoutPayload({ payload }));
              // paymentIntent contains authorized payment intent
              let paymentOption_ = { 'paymentIntentId': paymentIntent?.paymentIntent?.id };
              dispatch(updatePaymentIntentId(paymentOption_))
              setIsWalletUsed(true);
              completeWalletPurchase(paymentOption_, payload);
            }).catch((error) => {
              console.log(error);

              // handle error
            });
          }
        });
      }
    }
  };

  // A rejected handlePayment is recoverable only when the customer cancelled/closed the PayPal
  // popup — re-arming then lets them click again. Permanent mount/handler/config errors reject
  // immediately, so re-arming those would spin handlePayment in an endless loop.
  const isRecoverablePayPalError = (error) => {
    if (error == null) return false;
    const signal = `${error?.type ?? ''} ${error?.code ?? ''} ${error?.message ?? ''} ${error?.name ?? ''}`.toLowerCase();
    // Only an explicit customer cancellation is recoverable (re-arm for another click). A user closing
    // the popup reports as "...closed..." (e.g. popup_closed_by_user) and is caught here. We deliberately
    // do NOT match 'popup'/'window': a popup-BLOCKER error also contains those words but is a permanent
    // condition — matching it would silently re-arm with no feedback, so it falls through to the terminal
    // branch and surfaces an error telling the customer to allow popups.
    return signal.includes('cancel') || signal.includes('closed');
  }

  const armPayPalHandler = (paypalHandler) => {
    paypalHandler.handlePayment({
      error: function (d) {
        console.log("error", d);
        console.log(d?.active_payment_attempt?.error_detail);
        dispatch(displayError({ 'error': d?.active_payment_attempt?.error_text }));
      },
    }).then((paymentIntentResult) => {
      const sa = paymentIntentResult?.payer_info?.shipping_address;
      const paymentIntentId = paymentIntentResult?.id;

      const isCompleteAddress = sa
        && sa.firstName && sa.lastName
        && sa.addressLine1
        && sa.city && sa.state && sa.zip
        && sa.countryCode;

      if (!isCompleteAddress) {
        // PayPal didn't return a complete address — route to billing details form to collect/complete
        if (sa) {
          // Stash whatever partial data PayPal gave us so addBillingDetails can pre-fill the form
          const partialBilling = {
            firstName: sa.firstName ?? '',
            lastName: sa.lastName ?? '',
            addressLine1: sa.addressLine1 ?? '',
            addressLine2: sa.addressLine2 ?? '',
            city: sa.city ?? '',
            state: sa.state ?? '',
            zip: sa.zip ?? '',
            country: sa.countryCode ?? ''
          };
          dispatch(setCheckoutPayload({ payload: { ...checkoutPayload, billingDetail: partialBilling } }));
        }
        // Only the paymentIntentId is put in Redux. The password is intentionally NOT captured here:
        // the cart slice is persisted to sessionStorage and exposed in devtools, so credentials must
        // not pass through it. NEW users re-enter their password on the addbillingdetails fallback.
        dispatch(setPendingPayPalPaymentIntentId({ paymentIntentId }));
        setIsProcessingPayPalPayment(false);
        navigate('/checkout/addbillingdetails');
        return;
      }

      setIsWalletUsed(true);
      setIsProcessingPayPalPayment(true);

      const billingDetail = {
        firstName: sa.firstName,
        lastName: sa.lastName,
        addressLine1: sa.addressLine1,
        addressLine2: sa.addressLine2 ?? null,
        city: sa.city,
        state: sa.state,
        zip: sa.zip,
        country: sa.countryCode
      };
      const payload = { ...checkoutPayload, billingDetail };
      dispatch(setCheckoutPayload({ payload }));

      // Refresh the estimate so tax for the PayPal address is displayed, but do NOT update the payment
      // intent here. Unlike Apple Pay (which resyncs the amount before authorization), handlePayment
      // only resolves once the intent is already authorized, and Chargebee rejects updates to an
      // authorized intent. The authorized intent is consumed as-is, same as the addbillingdetails
      // fallback that EUR buyers (whose addresses usually have no state) have been completing through.
      dispatch(fetchEstimateForNewSubscription(payload)).then(() => {
        dispatch(setDisplayTax({ "displayTax": true }));
        const paymentOption_ = { 'paymentIntentId': paymentIntentId };
        dispatch(updatePaymentIntentId(paymentOption_));
        // Return the promise so a rejection from completeWalletPurchase's awaited reCAPTCHA path
        // propagates to the .catch below (clears PayPal processing state) instead of being dropped.
        return completeWalletPurchase(paymentOption_, payload);
      }).catch((error) => {
        setIsProcessingPayPalPayment(false);
        dispatch(displayError(error));
      });
    }).catch((error) => {
      if (isRecoverablePayPalError(error)) {
        // Customer cancelled/closed the popup — re-arm so the next button click starts a fresh attempt.
        armPayPalHandler(paypalHandler);
      } else {
        // Permanent failure — reset state and stop retrying instead of looping on armPayPalHandler.
        setIsProcessingPayPalPayment(false);
        dispatch(displayError({ 'error': parseError(error) ?? error?.message }));
      }
    });
  };

  const initPayPalCheckout = (payment_intent) => {
    if (cbInstance) {
      if (paypalHandler_ == null) {
        cbInstance.load("paypal").then((paypalHandler) => {
          setPaypalHandler_(paypalHandler);
          paypalHandler.setPaymentIntent(payment_intent);
          setDisplayPayWithCardLabel(true);
          paypalHandler.mountPaymentButton("#paypal-button", {
            style: {
              layout: 'horizontal',
              color: 'gold',
              shape: 'rect',
              label: 'paypal'
            }
          }).then(() => {
            armPayPalHandler(paypalHandler);
          }).catch((error) => {
            console.log('paypal mount failed', error);
          });
        });
      }
    }
  };

  const getPromoCode = () => {
    let promoCodeElement = document.getElementById('promo-code');
    if (promoCodeElement == null)
      return [];
    return promoCodeElement.value.split(',').filter(value => value.trim() !== '');
  }

  const mountPaymentMethod = (module_name, payment_method_type, button_selector, options) => {
    let paymentHandler;
    if (cbInstance) {
      cbInstance.load(module_name).then((paymentHandler_) => {
        paymentHandler = paymentHandler_;
        createPaymentIntent({ payment_method_type })
          .then((payment_intent) => {
            paymentHandler.setPaymentIntent(payment_intent);
            return paymentHandler.mountPaymentButton(button_selector, options);
          })
          .then(() => {
            // once button mounted
            return paymentHandler.handlePayment();
          })
          .then((paymentIntent) => {
            dispatch(updatePaymentIntentId({ 'paymentIntentId': paymentIntent.id }))
            // navigate("/checkout/createaccount");
            setIsWalletUsed(true);
          })
          .catch((error) => {
            console.log(error)
            // handle error
          });

      });
    }
  };

  const fetchEstimate = (payload = checkoutPayload) => {
    setError("");
    dispatch(fetchEstimateForNewSubscription(payload)).then((response) => {
      dispatch(setDisplayTax({ "displayTax": true }));
      updateApplePayHandlerWithNewAmount(response?.total);
    }).catch((error) => {
      dispatch(displayError(error));
      setError(error);
    })
  }

  const updateApplePayHandlerWithNewAmount = (amount, applePayHandler = applePayHandler_) => {
    if (applePayHandler != null && applePayHandler?.paymentIntent != null) {
      if (applePayHandler?.paymentIntent?.amount !== amount) {
        updatePaymentIntent(applePayHandler?.paymentIntent, amount).then((payment_intent) => {
          applePayHandler.setPaymentIntent(payment_intent);
        })
      }
    }
  }

  const fetchEstimateOnPromoCodeChange = (e) => {
    let promoCode = getPromoCode();
    let payload = { ...checkoutPayload, promoCode };
    dispatch(setCheckoutPayload({ payload }));
    fetchEstimate(payload);
  }

  const getCheckoutPayload = (applePayBillingContact) => {
    let billingAddress = {};

    let address1 = applePayBillingContact?.addressLines[0] ? applePayBillingContact?.addressLines[0] : applePayBillingContact?.locality;

    billingAddress = {
      "firstName": `${applePayBillingContact?.givenName}`,
      "lastName": `${applePayBillingContact?.familyName}`,
      "addressLine1": `${address1}`,
      "addressLine2": null,
      "city": `${applePayBillingContact?.locality}`,
      "state": `${applePayBillingContact?.administrativeArea}`,
      "zip": `${applePayBillingContact?.postalCode}`,
      "country": `${applePayBillingContact?.countryCode ?? applePayBillingContact?.country}`
    }
    let payload = {
      ...checkoutPayload, 'billingDetail': billingAddress
    };

    let promoCode = getPromoCode();

    if (promoCode && promoCode.length > 0) {
      payload = { ...payload, promoCode };
    }

    dispatch(setCheckoutPayload({ payload }));
    return payload;
  }

  const handleSubmit = async (event) => {
    if (event != null)
      event.preventDefault();

    // Refuse to submit until the gateway assignment has resolved. sessionIs3dsEnabled is null while
    // unresolved, and the branch below is a truthiness check — so without this guard an unresolved
    // assignment would fall through to tokenize and quietly put the customer on Worldpay, whichever
    // arm they actually belong to. The form is hidden in that state, so this is a backstop rather
    // than something a customer can reach.
    if (!assignmentResolved) {
      return;
    }

    if (validateForm("create-account__form")) {
      if (!isWalletUsed) {
        if (cardRef.current) {
          var token = null;
          try {
            setDisableBtn(true);
            setSubmitPhase(SUBMIT_PHASE.AUTHORIZING);
            // Start the captcha now rather than after the card is authorized. Nothing in it depends
            // on the card result, and on the 3DS path authorization is a round trip to the issuer
            // that takes seconds — running the two in parallel takes the captcha off the critical
            // path entirely.
            const pendingCaptcha = startRecaptchaAssessment("checkout");
            let paymentOption_ = null;
            if (sessionIs3dsEnabled) {
              // Reuse the mount-time intent only while it is untouched — see cardIntentSpent. A
              // retry gets a new one, which also picks up the current total rather than the amount
              // the page loaded with.
              let intentForAttempt = paymentIntent?.card;
              if (cardIntentSpent.current || !intentForAttempt) {
                intentForAttempt = await createPaymentIntent('card');
              }
              cardIntentSpent.current = true;
              let payment_intent = await cardRef.current.authorizeWith3ds(intentForAttempt);
              paymentOption_ = { 'paymentIntentId': payment_intent.id };
              dispatch(updatePaymentIntentId(paymentOption_));
            } else {
              token = await cardRef.current.tokenize(event);
              paymentOption_ = { 'paymentToken': token }
              dispatch(updatePaymentToken(paymentOption_))
            }
            // The card is authorized from here on, so the next step is the charge itself. The phase
            // change is what swaps the message to "don't close this page" — only true once the
            // purchase is actually in flight.
            setSubmitPhase(SUBMIT_PHASE.COMPLETING);
            completePurchase(paymentOption_, checkoutPayload, pendingCaptcha);
            // navigate("/checkout/createaccount");
          } catch (error) {
            setDisableBtn(false);
            setSubmitPhase(null);
            console.log(error)
          }
        } else {
          completePurchase({});
        }
      } else {
        completePurchase(paymentOption);
      }
    }
  };

  const completeWalletPurchase = async (paymentOption, payload) => {
    return completePurchase(paymentOption, payload);
  }

  const updatePassword = (e) => {
    setPassword(e.target.value);
    updateApplePayBtn();
  }

  const updateConfirmPassword = (e) => {
    setConfirmPassword(e.target.value);
    updateApplePayBtn();
  }

  // pendingCaptcha is an already-started reCAPTCHA assessment from the card path. Wallet and
  // no-card callers pass nothing and the assessment is started inside the thunk as before.
  const completePurchase = (paymentOption, payload = checkoutPayload, pendingCaptcha = null) => {
    if (validateForm("create-account__form")) {
      if (paymentOption?.paymentToken)
        payload = { ...payload, PaymentTokenId: paymentOption.paymentToken };
      else if (paymentOption?.paymentIntentId)
        payload = { ...payload, PaymentIntentId: paymentOption.paymentIntentId };
      else if (paymentOption?.paymentSourceId)
        payload = { ...payload, paymentSourceId: paymentOption.paymentSourceId };
      setDisableBtn(true);
      setErrorMessage("");
      return dispatch(createSubscriptionWithCaptcha(payload, "checkout", pendingCaptcha)).then((response) => {
        dispatch(setEcommerceGTMEvent({ ecommerceGtmEvent: response?.subscriptionGTMEvent }))
        createSalesforceCampaign(payload?.billingDetail);
        dispatch(updateDisplayPurchaseComplete({ displayPurchaseComplete: true }))

        if (userStatus === "NEW") {
          let password1 = document.getElementById('password').value;
          let password2 = document.getElementById('password2').value;
          dispatch(createAccountForNewUser({ email, password: password1, confirmPassword: password2 })).then((response) => {
            navigate("/checkout/thankyou")
          }).catch((error) => {
            setDisableBtn(false);
            setSubmitPhase(null);
            setIsProcessingPayPalPayment(false);
            dispatch(displayError(error));
          })
        }
        else
          navigate("/checkout/thankyou")

      }).catch((error) => {
        setDisableBtn(false);
        setSubmitPhase(null);
        setIsProcessingPayPalPayment(false);
        if (error?.Param === "payment")
          setErrorMessage(error?.Error)
        else
          dispatch(displayError(error));
      })

    }
  };

  useEffect(() => {
    // dispatch(fetchRenewalEstimate(email)).catch((error) => {
    //     displayError(error);
    // })

    checkForTemporaryPassword();

    focusAndOpenKeyboard(document.getElementById('password'), 300);

    const handleClick = (event) => {
      const eyeIcons = document.querySelectorAll('.personal-details__icon--eye');

      eyeIcons.forEach((item) => {
        item.classList.toggle('crossed');
        if (item.classList.contains('crossed')) {
          item.nextElementSibling.type = 'text';
          item.querySelector('span').textContent = t_label?.['account.label.hide_password'] ?? 'Hide password';
        } else {
          item.nextElementSibling.type = 'password';
          item.querySelector('span').textContent = t_label?.['account.label.show_password'] ?? 'Show password';
        }
      });
    };

    setTimeout(() => {
      const eyeIcons = document.querySelectorAll('.personal-details__icon--eye');
      console.log(eyeIcons);
      eyeIcons.forEach((item) => {
        item.addEventListener('click', handleClick);
      });
    }, 3000);


    return () => {
      const eyeIcons = document.querySelectorAll('.personal-details__icon--eye');
      eyeIcons.forEach((item) => {
        item.removeEventListener('click', handleClick);
      });
    };
  }, []);

  const checkForTemporaryPassword = () => {
    if (email) {
      dispatch(checkUserStatus(email)).then((response) => {
        if (response?.userStatus === "FORCE_CHANGE_PASSWORD") {
          setDisplayTemporaryPasswordMsg(true);
        }
      })
    }
  }

  const createSalesforceCampaign = ({ firstName, lastName }, checkoutStatus = CHECKOUT_STATUS.COMPLETED) => {
    let payload = salesforcePayload({ firstName, lastName, email, checkoutStatus });
    if (gdprEmailUpdates !== undefined) {
      payload = { ...payload, 'OptinMarketing': gdprEmailUpdates ? "YES" : "NO" }
    }
    return dispatch(createCampaignMember(payload))
  }

  const getPeriodText2 = (periodUnit) => {
    if (periodUnit) {
      if (periodUnit === "Month")
        return t_label?.['checkout.label.monthly'] ?? "monthly"
      else if (periodUnit === "Year")
        return t_label?.['checkout.label.annual'] ?? "annual"
      else if (periodUnit === "Day")
        return t_label?.['checkout.label.day'] ?? "day"
    }
    return null;
  }

  const updateApplePayBtn = () => {
    if (userPaymentMethodPreference === USER_PAYMENT_METHOD_PREFERENCE.APPLE_PAY || userPaymentMethodPreference === USER_PAYMENT_METHOD_PREFERENCE.PAYPAL) {
      setDisableBtn(!validateForm("create-account__form", true));
    }
  }


  return (
    displayPage ?
      <>
        <CheckoutProgress phase={submitPhase} labels={{
          authorizing: t_message?.['checkout.message.verifying_card'],
          authorizingSub: t_message?.['checkout.message.verifying_card_sub'],
          completing: t_message?.['checkout.message.completing_purchase'],
          completingSub: t_message?.['checkout.message.completing_purchase_sub']
        }} />
        {
          cbInstance &&
          <div className="body body--alt2">
            <Header />

            <main className="main main--fullh">
              <section className="section personal-details">
                <div className="grid-cont">


                  <form onSubmit={handleSubmit} noValidate id="create-account__form" className="personal-details__form">
                    <h2 className="personal-details__heading">
                      {
                        userStatus === "NEW" ?
                          t_label?.['checkout.label.confirm_purchase_and_create_account'] ?? 'Confirm Purchase & Create Account'
                          :
                          t_label?.['checkout.label.confirm_purchase'] ?? 'Confirm Purchase'
                      }
                    </h2>

                    <label className="personal-details__label">{t_label?.['checkout.label.your_email'] ?? 'Your Email'}</label>
                    <div className="form-group personal-details__control personal-details__control--hastip">
                      <div className="personal-details__icon personal-details__icon--prefix">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11.5 7.5H4.5C3.94772 7.5 3.5 7.94772 3.5 8.5V12C3.5 12.5523 3.94772 13 4.5 13H11.5C12.0523 13 12.5 12.5523 12.5 12V8.5C12.5 7.94772 12.0523 7.5 11.5 7.5Z" />
                          <path d="M5.5 7.5V5.5C5.5 4.83696 5.76339 4.20107 6.23223 3.73223C6.70107 3.26339 7.33696 3 8 3C8.66304 3 9.29893 3.26339 9.76777 3.73223C10.2366 4.20107 10.5 4.83696 10.5 5.5V7.5" />
                        </svg>
                        <div className="personal-details__tooltip">
                          <span>{t_message?.['checkout.message.fixed_email'] ?? "This email address was entered earlier in the process and cannot be changed. If you would like to use another email address, please start over."}</span>
                        </div>
                      </div>
                      <input disabled type="email" value={email} name="email" id="email" className="personal-details__input" />
                    </div>
                    {userStatus === "NEW" &&
                      <>
                        <label className="personal-details__label">{t_label?.['checkout.label.new_password'] ?? 'New Password'}</label>
                        <div className="form-group personal-details__control">
                          <button type="button" tabIndex="-1" className="personal-details__icon personal-details__icon--button personal-details__icon--postfix personal-details__icon--eye">
                            <svg width="18" height="16" viewBox="0 0 18 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1.66406 8.00033C1.66406 8.00033 4.33073 2.66699 8.9974 2.66699C13.6641 2.66699 16.3307 8.00033 16.3307 8.00033C16.3307 8.00033 13.6641 13.3337 8.9974 13.3337C4.33073 13.3337 1.66406 8.00033 1.66406 8.00033Z" />
                              <path d="M9 10C10.1046 10 11 9.10457 11 8C11 6.89543 10.1046 6 9 6C7.89543 6 7 6.89543 7 8C7 9.10457 7.89543 10 9 10Z" />
                            </svg>
                            <div className="personal-details__tooltip personal-details__tooltip--auto">
                              <span>{t_label?.['account.label.show_password'] ?? 'Show password'}</span>
                            </div>
                          </button>
                          <input required type="password" name="password" id="password" className="personal-details__input" onChange={updatePassword} data-pristine-required-message={t_message?.['checkout.message.required'] ?? 'This field is required'} />
                        </div>

                        <label className="personal-details__label">{t_label?.['account.label.confirm_password'] ?? 'Confirm Password'}</label>
                        <div className="form-group personal-details__control">
                          <button type="button" tabIndex="-1" className="personal-details__icon personal-details__icon--button personal-details__icon--postfix personal-details__icon--eye">
                            <svg width="18" height="16" viewBox="0 0 18 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1.66406 8.00033C1.66406 8.00033 4.33073 2.66699 8.9974 2.66699C13.6641 2.66699 16.3307 8.00033 16.3307 8.00033C16.3307 8.00033 13.6641 13.3337 8.9974 13.3337C4.33073 13.3337 1.66406 8.00033 1.66406 8.00033Z" />
                              <path d="M9 10C10.1046 10 11 9.10457 11 8C11 6.89543 10.1046 6 9 6C7.89543 6 7 6.89543 7 8C7 9.10457 7.89543 10 9 10Z" />
                            </svg>
                            <div className="personal-details__tooltip personal-details__tooltip--auto">
                              <span>{t_label?.['account.label.show_password'] ?? 'Show password'}</span>
                            </div>
                          </button>
                          <input required type="password" name="password2" data-pristine-equals="#password" data-pristine-equals-message={t_message?.['account.message.password_not_matching']} data-pristine-required-message={t_message?.['checkout.message.required'] ?? 'This field is required'} id="password2" className="personal-details__input" onChange={updateConfirmPassword} />
                        </div>

                      </>
                    }

                    {
                      userPaymentMethodPreference === USER_PAYMENT_METHOD_PREFERENCE.APPLE_PAY && (promoCode == null || promoCode.length < 2) &&
                      <>
                        <div className="grid-row mt-4">
                          <div className="grid-col sm:grid-col--12">
                            <div className="form-group promo-code">
                              <label htmlFor="promo-code" className="personal-details__label mt-0">{t_label?.['checkout.label.enter_a_promo_code'] ?? 'Enter a Promo Code'}</label>
                              <input
                                type="text"
                                name="promoCode"
                                defaultValue={promoCode}
                                id="promo-code"
                                placeholder={t_label?.['checkout.label.enter_a_promo_code'] ?? 'Enter a Promo Code'}
                                className="personal-details__input"
                                data-pristine-maxLength="50"
                                data-pristine-maxlength-message={t_message?.['checkout.message.promo_code_min_len'] ?? "Promo code cannot have more than 50 characters"}
                                onBlur={fetchEstimateOnPromoCodeChange}
                              />

                              {
                                error != null && error.Param === 'promocode' &&
                                <ErrorAlert error={parseError(error)} bgcolor='#f7f8f9' />
                              }
                            </div>

                          </div>
                        </div>
                      </>
                    }

                    {
                      !(parseFloat(total) === 0 && thankyouPageType === "COURSE") &&
                      userPaymentMethodPreference === USER_PAYMENT_METHOD_PREFERENCE.CARD &&

                      <>

                        <div className='mt-8'>
                          <h2 className="personal-details__heading">{t_label?.['billing.label.payment_details']}</h2>
                          <h5 className='payment-detail__subtitle'>{t_label?.['checkout.label.choose_payment_method']}</h5>

                          <div className='flex-container flex-col'>

                            {/* {
                              userPaymentMethodPreference === USER_PAYMENT_METHOD_PREFERENCE.APPLE_PAY &&
                              <div
                                id='apple-pay-button'
                                className='payment-details__button'
                                style={displayApplePayBtn ? {} : { 'display': 'none' }}
                              >
                              </div>
                            } */}

                            {/* {
                              userPaymentMethodPreference === USER_PAYMENT_METHOD_PREFERENCE.GOOGLE_PAY &&
                              <div
                                id='gpay-button'
                                className='payment-details__button'
                                style={paymentIntent?.google_pay ? {} : { 'display': 'none' }}
                              >
                              </div>
                            } */}

                          </div>
                          <div
                            className='flex-container flex-col payment-detail__label'
                            style={displayPayWithCardLabel ? {} : { 'display': 'none' }}
                          >
                            {/* {t_label?.['checkout.label.pay_with_card']} */}
                          </div>

                        </div>

                        < CBCardComponent ref={cardRef} />


                      </>
                    }


                    {errorMessage && <div className="cc-error" role="alert">{errorMessage}</div>}

                    {
                      subStatus === "InTrial" &&
                      <label className="form-group personal-details__checkbox">
                        <input id='agreement' required type="checkbox" name="agreement"
                          onChange={updateApplePayBtn}
                        />

                        <Localization text={thankyouPageType != 'ADD_ON' ? t_message?.['checkout.message.subscription_trial_billing'] : t_message?.['checkout.message.subscription_billing_one_time']} values={{
                          trialPeriod,
                          trialPeriodUnit: trialPeriodUnit.toLowerCase(),
                          amount: displayPrice(nextTotal, currencyCode),
                          periodUnit: getPeriodText2(periodUnit)?.toLowerCase()
                        }}
                        />
                        &nbsp;
                        <Localization text={t_message?.['checkout.message.subscription_trial_billing_acknowledge']} values={{
                          trialPeriod,
                          trialPeriodUnit: trialPeriodUnit.toLowerCase(),
                          periodUnit: getPeriodText2(periodUnit)?.toLowerCase()
                        }}
                        />

                      </label>
                    }

                    <label className="form-group personal-details__checkbox">
                      <input id='accept_tos' required type="checkbox" name="accept_tos"
                        onChange={updateApplePayBtn}
                      />
                      <span dangerouslySetInnerHTML={{ __html: t_message?.['checkout.message.agree_to_policy'] }} />
                    </label>

                    {
                      userPaymentMethodPreference === USER_PAYMENT_METHOD_PREFERENCE.CARD &&
                      <button disabled={disableBtn} type="submit" className={`button payment-details__submit ${disableBtn ? 'disabled' : ''}`}>{t_label?.['checkout.label.complete_purchase']}</button>
                    }

                    {
                      userPaymentMethodPreference === USER_PAYMENT_METHOD_PREFERENCE.APPLE_PAY && displayApplePayBtn &&
                      <div
                        onClick={(e) => validateForm("create-account__form")}
                      >
                        <div
                          style={disableBtn ? { 'pointerEvents': 'none' } : {}}
                        >
                          <div
                            id='apple-pay-button'
                            className='payment-details__button'
                          >
                          </div>
                        </div>
                      </div>
                    }

                    {
                      userPaymentMethodPreference === USER_PAYMENT_METHOD_PREFERENCE.PAYPAL &&
                      <div
                        onClick={(e) => validateForm("create-account__form")}
                      >
                        <div
                          style={(disableBtn || isProcessingPayPalPayment) ? { 'pointerEvents': 'none', opacity: 0.5 } : {}}
                        >
                          <div
                            id='paypal-button'
                            className='payment-details__button'
                            style={isProcessingPayPalPayment ? { display: 'none' } : {}}
                          >
                          </div>
                          {
                            isProcessingPayPalPayment &&
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', padding: '16px' }}>
                              <CircularProgress />
                              <div>{t_message?.['checkout.message.processing_payment'] ?? 'Processing your payment...'}</div>
                            </div>
                          }
                        </div>
                      </div>
                    }

                  </form>

                  <OrderSummary />

                </div>
              </section >
            </main >

            <Footer />

          </div >
        }
      </>
      :
      <LinearProgress />
  )
}

export default AddPaymentMethod